// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Switch} from '@wandb/weave/components';
import {RunState} from '@wandb/weave/components/RunState';
import {Tag} from '@wandb/weave/components/Tag';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {useIssuesContext} from '../../../state/issues/IssuesContext';
import {useRunIssuesDashboard} from '../../../state/issues/useRunIssuesDashboard';
import {useLatestRef} from '../../../util/hooks/useLatestRef';
import {AccordionSection} from '../AccordionSection/AccordionSection';
import {useFloatingWorkspaceDrawerTransition} from '../FloatingWorkspaceDrawer';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {EmptyRunIssuesDrawer} from './EmptyRunIssuesDrawer';
import {ErrorRunIssuesDrawer} from './ErrorRunIssuesDrawer';
import {getPriorityIssues} from './getPriorityIssues';
import {GrafanaLinkBanner} from './GrafanaBanner';
import {IssuesWelcomeBanner} from './IssuesWelcomeBanner';
import {checkIfCliIsOutdated} from './outdatedCLICheck';
import {OutdatedCliDrawer} from './OutdatedCliDrawer';
import {GroupedRunIssuesList, RunIssuesList} from './RunIssue';

export const RunIssuesDrawer = () => {
  const {
    activeErrorDrawer,
    activeIssues = [],
    activeRun,
    annotationActive,
    clearIssues,
    hoveredIssue,
    groupedIssues,
    selectedIssue,
    setSelectedIssue,
    toggleIssues,
    updateErrorIssue,
    workspaceType,
  } = useIssuesContext();

  const outdatedSDK = checkIfCliIsOutdated(activeRun);

  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  const {closeWorkspaceDrawer, contentType} = useWorkspaceDrawerContext();
  const [targetSection, setTargetSection] = useState<'overview' | 'all-issues'>(
    'overview'
  );

  const headerRef = useRef<HTMLDivElement>(null);
  const stableContentType = useLatestRef(contentType);
  const stableSelectedIssue = useLatestRef(selectedIssue);
  const stableTargetSection = useLatestRef(targetSection);

  const {data: runGrafanaDashboard} = useRunIssuesDashboard({
    entityName,
    projectName,
    runName: activeRun?.name,
  });

  useEffect(() => {
    return () => {
      // In the multi-run workspace we want to make sure to clear the issues per run
      if (workspaceType !== 'single-run') {
        clearIssues();
      }
    };
  }, [clearIssues, workspaceType]);

  // 'final' and 'before' are set by the clickable icons in Layout on the <IssueAlert> component
  const handleScrollToIssue = useCallback(() => {
    if (!stableSelectedIssue.current) {
      return;
    }

    const isLiveIssue =
      stableSelectedIssue.current !== 'final' &&
      stableSelectedIssue.current !== 'before';
    if (
      contentType === 'run-issues' &&
      stableSelectedIssue.current &&
      isLiveIssue
    ) {
      const issueToScrollTo = document.getElementById(
        stableSelectedIssue.current
      );
      if (issueToScrollTo) {
        issueToScrollTo.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [contentType, stableSelectedIssue]);

  useFloatingWorkspaceDrawerTransition('transitionend', handleScrollToIssue);

  useEffect(() => {
    if (contentType !== 'run-issues') {
      setSelectedIssue(null);
    } else {
      if (
        stableSelectedIssue.current &&
        stableSelectedIssue.current !== 'final' &&
        stableSelectedIssue.current !== 'before'
      ) {
        setTargetSection('all-issues');
      } else {
        setTargetSection('overview');
      }
    }
  }, [contentType, setSelectedIssue, stableSelectedIssue]);

  useEffect(() => {
    if (
      !selectedIssue ||
      !stableContentType.current ||
      stableContentType.current !== 'run-issues' ||
      !stableTargetSection.current
    ) {
      return;
    }

    if (selectedIssue === 'final' || selectedIssue === 'before') {
      if (stableTargetSection.current !== 'overview') {
        setTargetSection('overview');
      }
      if (headerRef.current) {
        headerRef.current.scrollIntoView({behavior: 'smooth'});
      }
    } else {
      if (stableTargetSection.current !== 'all-issues') {
        setTargetSection('all-issues');
      }
      const issueToScrollTo = document.getElementById(selectedIssue);
      if (issueToScrollTo) {
        issueToScrollTo.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [selectedIssue, stableContentType, stableTargetSection]);

  /**
   * Clear out the blocking issue when the drawer closes. When clicking a new error in the run selector it will be reloaded
   */
  useEffect(() => {
    if (!workspaceType && activeErrorDrawer) {
      updateErrorIssue(false);
    }
  }, [updateErrorIssue, workspaceType, activeErrorDrawer]);

  const status: 'error' | 'empty' | 'issues' = useMemo(() => {
    if (activeErrorDrawer) {
      return 'error';
    }
    if (activeIssues.length === 0) {
      return 'empty';
    }
    return 'issues';
  }, [activeIssues, activeErrorDrawer]);

  const {priorityIssue, priorityIssueDescription} = useMemo(() => {
    const issue = getPriorityIssues(activeIssues, activeRun?.state ?? '');
    return {
      priorityIssue: issue,
      priorityIssueDescription:
        (issue[0]?.updatedDescription ? issue[0]?.updatedDescription : '') +
        ' ' +
        (issue[0]?.resolution ? issue[0]?.resolution : ''),
    };
  }, [activeIssues, activeRun?.state]);

  return (
    <div className="flex h-full w-full flex-col">
      <header
        className="flex-grow-0 border-moon-250 px-20 py-12"
        data-test="run-issues-drawer-header"
        ref={headerRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-xl font-semibold leading-10">Issues</h1>
            {!activeErrorDrawer && (
              <Tag label={activeIssues.length.toString()} color="moon" />
            )}
          </div>
          <div className="flex items-center gap-8">
            {workspaceType === 'single-run' && (
              <>
                <label htmlFor="annotate-issues-switch">Annotate issues</label>
                <Switch.Root
                  checked={annotationActive}
                  id="annotate-issues-switch"
                  onCheckedChange={toggleIssues}
                  size="medium">
                  <Switch.Thumb checked={annotationActive} size="medium" />
                </Switch.Root>
              </>
            )}

            <Button
              startIcon="close"
              variant="ghost"
              onClick={() => closeWorkspaceDrawer(false)}
            />
          </div>
        </div>
      </header>
      {status === 'error' && !outdatedSDK && <ErrorRunIssuesDrawer />}
      {status === 'error' && outdatedSDK && (
        <OutdatedCliDrawer
          version={
            typeof activeRun?._wandb?.cli_version === 'string'
              ? activeRun?._wandb?.cli_version
              : ''
          }
        />
      )}
      {status !== 'error' && (
        <>
          <AccordionSection
            initialOpen={targetSection === 'overview'}
            title="Overview">
            <IssuesWelcomeBanner />

            {/* Note: to keep this from having a display race condition we render it even if we don't have the link. That link should never be unable to resolve before someone clicks the button, but preliminarily showing the banner makes this technically possible. */}
            <GrafanaLinkBanner link={runGrafanaDashboard ?? ''} />

            <div className="mt-16 flex h-24 items-center justify-start gap-8">
              <div className="text-[15px] font-semibold">Run</div>

              <RunState value={activeRun?.state ?? ''} />
            </div>
            <div className="mt-4">{activeRun?.displayName}</div>

            <div className="mb-4 mt-16 text-[15px] font-semibold">
              Issue summary
            </div>
            <p>
              CoreWeave continuously monitors infrastructure to ensure stable
              and reliable operations. Any critical issues will appear here.
            </p>

            <RunIssuesList hoveredIssue={hoveredIssue} issues={priorityIssue} />
            {priorityIssueDescription && (
              <div className="mt-16">{priorityIssueDescription}</div>
            )}
          </AccordionSection>
          <AccordionSection
            initialOpen={targetSection === 'all-issues'}
            title="All issues">
            {status === 'empty' && <EmptyRunIssuesDrawer />}

            {status === 'issues' && (
              <div className=" mt-16" data-test="run-issues-drawer-issues">
                <GroupedRunIssuesList
                  hoveredIssue={hoveredIssue}
                  groupedIssues={groupedIssues}
                />
              </div>
            )}
          </AccordionSection>
        </>
      )}
    </div>
  );
};
